import type { FC, JSX } from 'react';
import './styles.css';
import classNames from 'classnames';
import { makeBoxSubtree } from 'components/BoxTree/BoxSubtree';
import {
  type BoxTreeHocProps,
  CHILDREN_ORIENTATION_DEFAULT,
} from 'components/BoxTree/BoxTreeHocProps';
import { useMemoCollection } from 'utils/hooks/useMemoCollection';
import type { BoxTreeNodePath } from './BoxTreeNodeComponentProps';

type Props<TNode, TContext = undefined> = {
  rootNodes: TNode[];
} & (undefined extends TContext ? { context?: never } : { context: TContext });

export const makeBoxTree = <TNode, TContext = undefined>(
  hocProps: BoxTreeHocProps<TNode, TContext>,
): FC<Props<TNode, TContext>> => {
  const BoxSubtree = makeBoxSubtree(hocProps);

  const BoxTree = (props: Props<TNode, TContext>): JSX.Element => {
    const getSubtreePath = useMemoCollection(
      (index: number): BoxTreeNodePath => [index],
      [],
    );

    return (
      <div
        className={classNames(
          'flex',
          (hocProps.getChildrenOrientation?.(null) ??
            CHILDREN_ORIENTATION_DEFAULT) === 'horizontal'
            ? 'flex-row'
            : 'flex-col',
        )}
      >
        {props.rootNodes.map((node, index) => (
          <BoxSubtree
            context={props.context as TContext}
            path={getSubtreePath(index)}
            // biome-ignore lint/suspicious/noArrayIndexKey: no better key
            key={index}
            node={node}
            parentNode={null}
            topLevelLine={index < props.rootNodes.length - 1}
          />
        ))}
      </div>
    );
  };
  return BoxTree;
};
